import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, Tag, Package } from 'lucide-react';
import { cn } from '../../../lib/utils';

const ProductGrid = ({ products, loading, fetchingNormalizedId, onViewNormalizedClick, onProductClick }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
            <div className="aspect-[3/4] bg-gray-100 rounded-lg mb-4" />
            <div className="h-4 bg-gray-100 rounded w-3/4 mb-2" />
            <div className="h-4 bg-gray-100 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200">
        <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">No products found</h3>
        <p className="text-gray-500">Try adjusting your search or filter</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {products.map((product, index) => (
        <motion.div
          key={product.id || index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.05 }}
          className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 hover:border-brand-500/50"
        >
          <div className="relative aspect-[3/4] bg-gray-100 dark:bg-gray-800 overflow-hidden">
            {(() => {
              const displayImage = product.image ||
                (Array.isArray(product.images) ? product.images[0] : product.images) ||
                product.imageUrl;

              return displayImage ? (
                <img
                  src={displayImage}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <ShoppingBag className="w-12 h-12" />
                </div>
              );
            })()}

            {product.discount > 0 && (
              <div className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                -{product.discount}%
              </div>
            )}

            {product.processed && (
              <div className="absolute top-3 right-3">
                <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-sm">
                  Processed
                </span>
              </div>
            )}
          </div>

          <div className="p-4 dark:bg-gray-800">
            <div className="flex items-start justify-between gap-2 mb-2">
              <p className="text-xs font-medium text-brand-600 uppercase tracking-wide dark:text-white">
                {product.brand}
              </p>
              {product.inStock && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-50 text-green-700 border border-green-100 ">
                  In Stock
                </span>
              )}
            </div>

            <h3
              className={cn(
                "text-sm font-medium text-gray-900 line-clamp-2 mb-3 min-h-[2.5rem]",
                product.processed && "cursor-pointer hover:text-brand-600 dark:text-brand-400"
              )}
              title={product.name}
              onClick={() => onProductClick && onProductClick(product)}
            >
              {product.name}
            </h3>

            <div className="mt-auto">
              <div className="flex items-center justify-between mb-3">
                <div className="flex flex-col">
                  {product.originalPrice > product.price && (
                    <span className="text-xs text-gray-400 line-through dark:text-gray-100">
                      {product.currency || 'AED'} {product.originalPrice?.toLocaleString()}
                    </span>
                  )}
                  <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {product.currency || 'AED'} {product.price?.toLocaleString()}
                  </span>
                </div>

                <a
                  href={product.productUrl || product.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-400 hover:text-brand-600 dark:bg-gra-50 dark:text-gray-25 hover:bg-brand-50 dark:hover:bg-gray-400 rounded-full transition-colors"
                  title="View on Vendor Site"
                >
                  <Tag className="w-4 h-4" />
                </a>
              </div>

              {/* View Product Button */}
              <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
                <button
                  onClick={() => onViewNormalizedClick && onViewNormalizedClick(product)}
                  disabled={!product.processed || fetchingNormalizedId === product.id}
                  className={`w-full text-xs font-medium py-1.5 rounded-lg transition-colors flex items-center justify-center gap-1
                          ${product.processed
                      ? "bg-brand-50 hover:bg-brand-100 text-brand-700 dar:bg-brand-900/10 dark:hover:bg-brand-900/20 dark:text-brand-400"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500"
                    }
                      `}
                >
                  {fetchingNormalizedId === product.id ? "Loading..." : "View Product"}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default ProductGrid;
